import { Box, Chip, Typography } from '@mui/material';

export const DEFAULT_VETTING_THRESHOLDS = {
  acceptThreshold: 75,
  waitlistThreshold: 55,
};

const PENDING_DECISION_STYLE = {
  label: 'Pending',
  color: 'text.secondary',
  backgroundColor: 'rgba(117, 117, 117, 0.12)',
  chipBackgroundColor: 'rgba(117, 117, 117, 0.12)',
  chipColor: '#757575',
};

const DECISION_STYLES = {
  rejected: {
    label: 'Rejected',
    color: '#d94b57',
    backgroundColor: 'rgba(217, 75, 87, 0.12)',
    chipBackgroundColor: 'rgba(211, 47, 47, 0.12)',
    chipColor: '#d32f2f',
  },
  waitlisted: {
    label: 'Waitlisted',
    color: '#d69b65',
    backgroundColor: 'rgba(214, 155, 101, 0.14)',
    chipBackgroundColor: 'rgba(237, 108, 2, 0.12)',
    chipColor: '#ed6c02',
  },
  accepted: {
    label: 'Accepted',
    color: '#4f9d69',
    backgroundColor: 'rgba(79, 157, 105, 0.14)',
    chipBackgroundColor: 'rgba(46, 125, 50, 0.12)',
    chipColor: '#2e7d32',
  },
};

export const getVettingThresholds = (activeConfig) => {
  const acceptThreshold = Number(
    activeConfig?.acceptThreshold ?? DEFAULT_VETTING_THRESHOLDS.acceptThreshold,
  );
  const waitlistThreshold = Number(
    activeConfig?.waitlistThreshold ?? DEFAULT_VETTING_THRESHOLDS.waitlistThreshold,
  );

  return {
    acceptThreshold: Number.isFinite(acceptThreshold)
      ? acceptThreshold
      : DEFAULT_VETTING_THRESHOLDS.acceptThreshold,
    waitlistThreshold: Number.isFinite(waitlistThreshold)
      ? waitlistThreshold
      : DEFAULT_VETTING_THRESHOLDS.waitlistThreshold,
  };
};

export const buildVettingDecisionThresholds = (thresholds = DEFAULT_VETTING_THRESHOLDS) => {
  const accept = Math.min(100, Math.max(0, thresholds.acceptThreshold));
  const waitlist = Math.min(accept, Math.max(0, thresholds.waitlistThreshold));
  const waitlistUpper = Math.max(accept - 1, waitlist);

  return [
    {
      ...DECISION_STYLES.rejected,
      range: `< ${waitlist}`,
      width: `${waitlist}%`,
    },
    {
      ...DECISION_STYLES.waitlisted,
      range: `${waitlist}-${waitlistUpper}`,
      width: `${Math.max(accept - waitlist, 0)}%`,
    },
    {
      ...DECISION_STYLES.accepted,
      range: `>= ${accept}`,
      width: `${Math.max(100 - accept, 0)}%`,
    },
  ];
};

const getThresholdStyle = (label, thresholds) => {
  const items = buildVettingDecisionThresholds(thresholds);
  return items.find((item) => item.label === label) ?? PENDING_DECISION_STYLE;
};

export const getVettingDecisionMeta = (
  score,
  decision,
  thresholds = DEFAULT_VETTING_THRESHOLDS,
) => {
  const normalizedDecision = String(decision ?? '').toLowerCase();

  if (normalizedDecision.includes('accepted')) return getThresholdStyle('Accepted', thresholds);
  if (normalizedDecision.includes('waitlist')) return getThresholdStyle('Waitlisted', thresholds);
  if (normalizedDecision.includes('reject')) return getThresholdStyle('Rejected', thresholds);

  if (!Number.isFinite(score)) return PENDING_DECISION_STYLE;
  if (score >= thresholds.acceptThreshold) return getThresholdStyle('Accepted', thresholds);
  if (score >= thresholds.waitlistThreshold) return getThresholdStyle('Waitlisted', thresholds);
  return getThresholdStyle('Rejected', thresholds);
};

export const getVettingScoreMeta = (score, thresholds = DEFAULT_VETTING_THRESHOLDS) => {
  const numericScore = Number(score);
  return getVettingDecisionMeta(
    Number.isFinite(numericScore) ? numericScore : null,
    null,
    thresholds,
  );
};

export const VettingStatusChip = ({ label, config }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      height: 24,
      fontWeight: 600,
      fontSize: '0.75rem',
      bgcolor: config.chipBackgroundColor,
      color: config.chipColor,
      borderRadius: '6px',
      textTransform: 'capitalize',
    }}
  />
);

export const DecisionThresholdBar = ({
  score,
  decision,
  thresholds = DEFAULT_VETTING_THRESHOLDS,
}) => {
  const numericScore = Number(score);
  const hasScore = Number.isFinite(numericScore);
  const boundedScore = hasScore ? Math.min(100, Math.max(0, numericScore)) : 0;
  const decisionMeta = getVettingDecisionMeta(hasScore ? boundedScore : null, decision, thresholds);
  const decisionThresholds = buildVettingDecisionThresholds(thresholds);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: decisionMeta.color,
        backgroundColor: decisionMeta.backgroundColor,
        borderRadius: 2,
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Decision Status
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: decisionMeta.color, fontWeight: 700, textTransform: 'capitalize' }}
          >
            {decision || decisionMeta.label}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">
            Score
          </Typography>
          <Typography variant="h5" sx={{ color: decisionMeta.color, fontWeight: 800 }}>
            {hasScore ? boundedScore : '-'}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ position: 'relative', pt: 1.5 }}>
        <Box sx={{ display: 'flex', overflow: 'hidden', borderRadius: 999, height: 12 }}>
          {decisionThresholds.map((threshold) => (
            <Box
              key={threshold.label}
              sx={{
                width: threshold.width,
                bgcolor: threshold.color,
                opacity: threshold.label === decisionMeta.label ? 1 : 0.35,
              }}
            />
          ))}
        </Box>
        {hasScore && (
          <Box
            sx={{
              position: 'absolute',
              top: 2,
              left: `${boundedScore}%`,
              width: 10,
              height: 24,
              borderRadius: 999,
              bgcolor: 'background.paper',
              border: '2px solid',
              borderColor: decisionMeta.color,
              transform: 'translateX(-50%)',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.22)',
            }}
          />
        )}
      </Box>

      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 1 }}
      >
        {decisionThresholds.map((threshold) => (
          <Typography
            key={threshold.label}
            variant="caption"
            sx={{ color: threshold.color, whiteSpace: 'nowrap' }}
          >
            {threshold.label}: {threshold.range}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};
